import { Fragment, useEffect, useState } from "react";
import { api, ApiError } from "../api";
import { tg } from "../telegram";
import { useT } from "../i18n";
import { useTelegramBackButton } from "../hooks/useTelegramBackButton";
import { nf } from "../hooks/useNorms";
import type { NormId, NormItem, NormSaveOut, NormStats, Norms } from "../types";

type T = (k: string, v?: Record<string, string | number>) => string;

// Quick picks per norm; the ± stepper reaches everything else in the norm's step.
const PRESETS: Record<NormId, Partial<Norms>[]> = {
  v_band: [{ v_lo: 18.5, v_hi: 21.5 }, { v_lo: 19, v_hi: 21 }, { v_lo: 19.5, v_hi: 20.5 }],
  v_dip: [17, 17.5, 18, 18.5, 19].map(v => ({ v_dip: v })),
  trip_s: [3, 6, 10, 15].map(v => ({ trip_s: v })),
  f_band: [{ f_lo: 49, f_hi: 51 }, { f_lo: 49.5, f_hi: 50.5 }, { f_lo: 49.8, f_hi: 50.2 }],
  sharp_kv: [0.5, 1, 1.5, 2].map(v => ({ sharp_kv: v })),
  prolonged_s: [5, 10, 30, 60].map(v => ({ prolonged_s: v })),
};
const UNIT: Record<NormId, string> = { v_band: "norms.u_kv", v_dip: "norms.u_kv", trip_s: "norms.u_s", f_band: "norms.u_hz", sharp_kv: "norms.u_kv", prolonged_s: "norms.u_s" };
const SLOW: NormId[] = ["sharp_kv", "prolonged_s"];  // their preview scans every second (3–8 s)
const EVENT_NORMS: NormId[] = ["v_dip", "trip_s"];   // saving them re-detects the events
const WITH_STD: NormId[] = ["v_band", "v_dip"];      // a note on where the default comes from
const LABEL = { fontSize: 11, color: "var(--hint)", textTransform: "uppercase" as const, letterSpacing: ".06em", marginBottom: 6 };
const STEP = { width: 52, flex: "0 0 auto", minHeight: 48, padding: 0, fontSize: 24 };

/** A norm's value as a short label: «19–21 кВ», «ниже 18 кВ», «быстрее 6 с». */
export function normValue(id: NormId, v: Partial<Norms>, t: T, lang: string): string {
  const f = (x?: number) => (x == null ? "—" : nf(x, lang));
  switch (id) {
    case "v_band": return t("norms.v_band_val", { lo: f(v.v_lo), hi: f(v.v_hi) });
    case "v_dip": return t("norms.v_dip_val", { v: f(v.v_dip) });
    case "trip_s": return t("norms.trip_s_val", { v: f(v.trip_s) });
    case "f_band": return t("norms.f_band_val", { lo: f(v.f_lo), hi: f(v.f_hi) });
    case "sharp_kv": return t("norms.sharp_kv_val", { v: f(v.sharp_kv) });
    default: return t("norms.prolonged_s_val", { v: f(v.prolonged_s) });
  }
}

type Cell = string | number | null | undefined;
const pair = (a: Cell, b?: Cell, cand = false) => (cand ? `${a ?? "—"} → ${b ?? "—"}` : `${a ?? "—"}`);

/** Headline + per-unit rows: the saved values (a), or the saved values against a candidate (b). */
function compare(id: NormId, a: NormStats | null, b: NormStats | null, cand: boolean, t: T, lang: string) {
  const u = (s: NormStats | null, n: number) => s?.units?.find(x => x.unit === n);
  if (id === "trip_s") {
    return { head: `${t("norms.pv_trips")}: ${pair(a?.trips, b?.trips, cand)}`, rows: [[t("norms.pv_stops"), pair(a?.stops, b?.stops, cand)]] };
  }
  if (id === "v_band" || id === "f_band") {
    const val = (s: NormStats | null, n: number): Cell => {
      const x = u(s, n);
      if (!x) return null;
      return id === "v_band" ? (x.pct == null ? null : `${nf(x.pct, lang)} %`) : x.out;
    };
    return { head: t(id === "v_band" ? "norms.pv_band" : "norms.pv_freq"), rows: [1, 2].map(n => [`G${n}`, pair(val(a, n), val(b, n), cand)]) };
  }
  const head = id === "v_dip" ? "norms.pv_dips" : id === "sharp_kv" ? "norms.pv_sharp" : "norms.pv_prolonged";
  return { head: `${t(head)}: ${pair(a?.total, b?.total, cand)}`, rows: [1, 2].map(n => [`G${n}`, pair(u(a, n)?.n, u(b, n)?.n, cand)]) };
}

// Bottom sheet for one norm: ± and quick picks, what the value gives on all G1/G2 data
// (counted on the server 0.6 s after the last change), save (+ re-detect events) / defaults.
export default function NormSheet({ item, onClose, onSaved }: {
  item: NormItem; onClose: () => void; onSaved: (r: NormSaveOut & { id: NormId }) => void;
}) {
  const { t, lang } = useT();
  useTelegramBackButton(onClose, [onClose]);
  const [draft, setDraft] = useState<Partial<Norms>>(item.values);
  const [base, setBase] = useState<NormStats | null>(item.stats);
  const [pv, setPv] = useState<NormStats | null>(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const keys = item.keys;
  const changed = keys.some(k => draft[k] !== item.values[k]);
  const atDefault = keys.every(k => draft[k] === item.defaults[k]);
  const badRange = keys.length === 2 && !((draft[keys[0]] ?? 0) < (draft[keys[1]] ?? 0));
  const draftKey = keys.map(k => draft[k]).join("|");

  // The «Всплески» norms come without list stats (every second is scanned): count the saved value once.
  useEffect(() => {
    if (base) return;
    api.post<{ stats: NormStats }>("/api/norms/preview", { id: item.id, values: item.values }, 60_000)
      .then(r => setBase(r.stats)).catch((e: ApiError) => setErr(e.message));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setPv(null);
    if (!changed || badRange) return;
    let alive = true;
    const h = setTimeout(() => {
      setErr(null);
      api.post<{ stats: NormStats }>("/api/norms/preview", { id: item.id, values: draft }, 60_000)
        .then(r => { if (alive) setPv(r.stats); })
        .catch((e: ApiError) => { if (alive) setErr(e.message); });
    }, 600);
    return () => { alive = false; clearTimeout(h); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftKey]);

  const step = (k: keyof Norms, dir: number) => {
    const x = Math.round(((draft[k] ?? 0) + dir * item.step) * 100) / 100;
    setDraft(d => ({ ...d, [k]: Math.min(item.max, Math.max(item.min, x)) }));
    tg()?.HapticFeedback?.selectionChanged?.();
  };
  const save = async () => {
    setSaving(true); setErr(null);
    try {
      const r = await api.put<NormSaveOut>(`/api/norms/${item.id}`, { values: draft });
      onSaved({ ...r, id: item.id });
    } catch (e) {
      setErr((e as ApiError).message);
      tg()?.HapticFeedback?.notificationOccurred("error");
    } finally {
      setSaving(false);
    }
  };

  const waiting = changed ? !pv && !badRange : !base;
  const c = compare(item.id, base, pv, changed, t, lang);
  const delta = changed && base?.total != null && pv?.total != null ? pv.total - base.total : null;

  return (
    <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 200, display: "flex", alignItems: "flex-end" }} onClick={saving ? undefined : onClose}>
      <div style={{ background: "var(--bg)", width: "100%", maxHeight: "92vh", overflowY: "auto", borderRadius: "16px 16px 0 0", padding: 16 }} onClick={e => e.stopPropagation()}>
        <h2 style={{ marginTop: 0, marginBottom: 6 }}>{t("norms." + item.id)}</h2>
        <div style={{ fontSize: 13, color: "var(--hint)", marginBottom: 12 }}>{t("norms." + item.id + "_desc")}</div>

        {keys.map((k, i) => (
          <div key={k} style={{ marginBottom: 10 }}>
            <div style={LABEL}>{keys.length === 1 ? t("norms.threshold") : t(i === 0 ? "norms.lower" : "norms.upper")}</div>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12 }}>
              <button className="btn secondary" style={STEP} aria-label="−" disabled={saving} onClick={() => step(k, -1)}>−</button>
              <div style={{ fontSize: 28, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                {nf(draft[k] ?? 0, lang)} <span style={{ fontSize: 15, fontWeight: 500, color: "var(--hint)" }}>{t(UNIT[item.id])}</span>
              </div>
              <button className="btn secondary" style={STEP} aria-label="+" disabled={saving} onClick={() => step(k, 1)}>+</button>
            </div>
          </div>
        ))}
        {item.id === "v_dip" && (
          <div style={{ fontSize: 13, color: "var(--hint)", textAlign: "center", margin: "-4px 0 10px" }}>
            {t("norms.pct_nominal", { p: nf(((draft.v_dip ?? 0) / 20) * 100, lang) })}
          </div>
        )}
        <div style={{ display: "flex", gap: 6, marginBottom: 6 }}>
          {PRESETS[item.id].map((p, i) => {
            const on = keys.every(k => p[k] === draft[k]);
            return (
              <button key={i} className={"period-tab" + (on ? " active" : "")} style={{ flex: 1, padding: "8px 4px" }} disabled={saving}
                onClick={() => { setDraft(d => ({ ...d, ...p })); tg()?.HapticFeedback?.selectionChanged?.(); }}>
                {keys.map(k => nf(p[k] ?? 0, lang)).join("–")}
              </button>
            );
          })}
        </div>
        <div style={{ fontSize: 12, color: "var(--hint)", marginBottom: 12 }}>
          {t("norms.default_is", { v: normValue(item.id, item.defaults, t, lang) })}
          {WITH_STD.includes(item.id) ? " · " + t("norms." + item.id + "_std") : ""}
        </div>
        {badRange && <div className="alert error" style={{ marginBottom: 10 }}>{t("norms.bad_range")}</div>}

        <div className="card" style={{ marginBottom: 12 }}>
          <div style={LABEL}>{changed ? t("norms.if_saved") : t("norms.now")}</div>
          {waiting ? (
            <div style={{ fontSize: 14, color: "var(--hint)", padding: "4px 0" }}>
              <span className="spinner" /> {SLOW.includes(item.id) ? t("norms.counting_slow") : t("norms.counting")}
            </div>
          ) : !badRange && (
            <>
              <div style={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 8, fontSize: 18, fontWeight: 700, fontVariantNumeric: "tabular-nums" }}>
                <span>{c.head}</span>
                {delta != null && delta !== 0 && (
                  <span style={{ padding: "1px 8px", borderRadius: 999, fontSize: 12, fontWeight: 600, background: "var(--yellow)", color: "var(--yellow-text)" }}>
                    {delta > 0 ? `+${delta}` : `−${-delta}`}
                  </span>
                )}
              </div>
              <div style={{ display: "grid", gridTemplateColumns: "auto 1fr", columnGap: 12, rowGap: 2, marginTop: 6, fontSize: 14, fontVariantNumeric: "tabular-nums" }}>
                {c.rows.map(([l, v]) => <Fragment key={l}><span style={{ color: "var(--hint)" }}>{l}</span><span>{v}</span></Fragment>)}
              </div>
            </>
          )}
          <div style={{ fontSize: 12, color: "var(--hint)", marginTop: 8 }}>{t("norms.computed_on")}</div>
        </div>

        {err && <div className="alert error" style={{ marginBottom: 10 }}>{err}</div>}
        <button className="btn" disabled={!changed || badRange || saving} onClick={save}>
          {saving ? <><span className="spinner" /> {t("norms.saving")}</>
            : !changed ? t("norms.unchanged") : t(EVENT_NORMS.includes(item.id) ? "norms.save_rebuild" : "norms.save")}
        </button>
        {!atDefault && (
          <button className="btn secondary" style={{ marginTop: 8 }} disabled={saving} onClick={() => setDraft({ ...item.defaults })}>
            {t("norms.reset", { v: normValue(item.id, item.defaults, t, lang) })}
          </button>
        )}
        <button className="btn secondary" style={{ marginTop: 8 }} disabled={saving} onClick={onClose}>{t("common.close")}</button>
      </div>
    </div>
  );
}
